import {
  ValidatorFn,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { Injectable } from '@angular/core';
import { Constants } from 'src/app/constants';
import { TranslateService } from '@ngx-translate/core';
import { ValidatorService } from 'angular-iban';
import { OptionsEnum } from 'src/app/model/enums/OptionsEnum';
import { isValid, differenceInYears } from 'date-fns';
import { replaceThousandSeparatorAndRemovingEuroPrefix } from '../utils/utils';

@Injectable({
  providedIn: 'root',
})
export class ValidationService {
  constructor(private translateService: TranslateService) {}

  static MAX_COVER_LENGTH_IN_YEARS = 50;
  static MAX_ANNUITY_RATE = 8;
  static MAX_AGE = 75;
  static MIN_AGE = 18;
  static MIN_MONTH_AMOUNT = 1;
  static thousandSeparator = Constants.THOUSAND_SEPARATOR;


  public createEmailValidator(): ValidatorFn {
    return (control: FormControl) => {
      if (
        control.value &&
        !control.value.match(Constants.emailValidationPattern)
      ) {
        return {
          type: 'pattern',
          message: this.translateService.instant('Common.InvalidEmailFormat'),
        };
      } else {
        return null;
      }
    };
  }

  createDateOfBirthValidator(): ValidatorFn {
    return (control: FormControl) => {
      if (!(control.value && isValid(control.value))) {
        return null;
      }
      const diffInYears = differenceInYears(new Date(), control.value);
      if (
        diffInYears > ValidationService.MAX_AGE ||
        diffInYears < ValidationService.MIN_AGE
      ) {
        return {
          type: 'pattern',
          message: this.translateService.instant('Common.InvalidDateOfBirth'),
        };
      } else {
        return null;
      }
    };
  }

  createCoverTermLengthValidator(maxLengthInYears: number): ValidatorFn {
    return (control: FormControl) => {
      if (
        control.value &&
        control.value * 1 > maxLengthInYears
      ) {
        return {
          type: 'pattern',
          message: this.translateService.instant('Common.InvalidCoverTermLength', { maxLengthInYears }),
        };
      } else {
        return null;
      }
    };
  }

  createPhoneValidator(): ValidatorFn {
    return (control: FormControl) => {
      if (
        control.value &&
        !control.value.match(Constants.phonePattern)
      ) {
        return {
          type: 'pattern',
          message: this.translateService.instant('Common.MobileNumberFormat'),
        };
      } else {
        return null;
      }
    };
  }

  mustMatch(controlName: string, matchingControlName: string): (formGroup: FormGroup) => void {
    return (formGroup: FormGroup) => {
      const control = formGroup.controls[controlName];
      const matchingControl = formGroup.controls[matchingControlName];

      if (matchingControl.errors && !matchingControl.errors.mustMatch) {
        // return if another validator has already found an error on the matchingControl
        return;
      }

      // set error on matchingControl if validation fails
      if (control.value !== matchingControl.value) {
        matchingControl.setErrors({ mustMatch: true });
      } else {
        matchingControl.setErrors(null);
      }
    };
  }

  public mustMatchEmails(): ValidatorFn {
    return (control: FormControl) => {
      const email = control?.parent?.get('emailAddress')?.value;
      const confirmedEmail = control?.parent?.get('confirmedEmailAddress')
        ?.value;

      return email && confirmedEmail && email !== confirmedEmail
        ? { mustMatch: true }
        : null;
    };
  }

  clearFormGroupValidators(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach((key) => {
      formGroup.controls[key].clearValidators();
      formGroup.controls[key].updateValueAndValidity();
    });
  }

  clearFormGroupValidatorsWithNoEvents(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach((key) => {
      formGroup.controls[key].clearValidators();
      formGroup.controls[key].updateValueAndValidity({emitEvent: false});
    });
  }

  cleanPolicyHolderValidations(policyHolder: FormGroup): void {
    this.clearFormGroupValidators(policyHolder.get('insured') as FormGroup);
    policyHolder.get('insured').updateValueAndValidity();

    policyHolder.controls.organizationName.clearValidators();
    policyHolder.controls.organizationName.updateValueAndValidity();
  }

  setInsuredValidators(insuredFormGroup: FormGroup): void {
    insuredFormGroup.get('initials').setValidators(Validators.required);
    insuredFormGroup.get('surname').setValidators(Validators.required);
    insuredFormGroup.get('gender').setValidators(Validators.required);
    insuredFormGroup.get('zipCode').setValidators(Validators.required);
    insuredFormGroup.get('houseNumber').setValidators(Validators.required);
    insuredFormGroup.get('street').setValidators(Validators.required);
    insuredFormGroup.get('city').setValidators(Validators.required);
    insuredFormGroup
      .get('mobilePhoneNo')
      .setValidators([Validators.required, this.createPhoneValidator()]);
    insuredFormGroup.get('nationality').setValidators(Validators.required);
    insuredFormGroup
      .get('emailAddress')
      .setValidators([this.createEmailValidator(), Validators.required]);
    insuredFormGroup
      .get('confirmedEmailAddress')
      .setValidators([this.mustMatchEmails(), Validators.required]);

    insuredFormGroup
      .get('iban')
      .setValidators([Validators.required, ValidatorService.validateIban]);
  }

  cleanInsured2Validations(insuredFormGroup: FormGroup): void {
    insuredFormGroup.controls.zipCode.clearValidators();
    insuredFormGroup.controls.zipCode.updateValueAndValidity();

    insuredFormGroup.controls.houseNumber.clearValidators();
    insuredFormGroup.controls.houseNumber.updateValueAndValidity();

    insuredFormGroup.controls.street.clearValidators();
    insuredFormGroup.controls.street.updateValueAndValidity();

    insuredFormGroup.controls.city.clearValidators();
    insuredFormGroup.controls.city.updateValueAndValidity();
  }

  setInsured2Validators(insuredFormGroup: FormGroup): void {
    insuredFormGroup.get('initials').setValidators(Validators.required);
    insuredFormGroup.get('surname').setValidators(Validators.required);
    insuredFormGroup.get('gender').setValidators(Validators.required);
    insuredFormGroup
      .get('mobilePhoneNo')
      .setValidators([Validators.required, this.createPhoneValidator()]);
    insuredFormGroup
      .get('emailAddress')
      .setValidators([this.createEmailValidator(), Validators.required]);
    insuredFormGroup
      .get('confirmedEmailAddress')
      .setValidators([this.mustMatchEmails(), Validators.required]);
    this.setInsured2ValidatorsForSameAddressChanged(insuredFormGroup, insuredFormGroup.value.addressSameAsFirstInsured);
  }

  setInsured2ValidatorsForSameAddressChanged(insuredFormGroup: FormGroup, newSameAddress: OptionsEnum): void {
    if (newSameAddress === OptionsEnum.Yes) {
      this.cleanInsured2Validations(insuredFormGroup);
    } else {
      insuredFormGroup.get('zipCode').setValidators(Validators.required);
      insuredFormGroup.get('houseNumber').setValidators(Validators.required);
      insuredFormGroup.get('street').setValidators(Validators.required);
      insuredFormGroup.get('city').setValidators(Validators.required);
    }
  }

  setBeneficiaryValidations(beneficiaryFormGroup: FormGroup): void {
    beneficiaryFormGroup.get('relationToInsured').setValidators(Validators.required);
    beneficiaryFormGroup.get('initials').setValidators(Validators.required);
    beneficiaryFormGroup.get('surname').setValidators(Validators.required);
    beneficiaryFormGroup.get('gender').setValidators(Validators.required);
    beneficiaryFormGroup.get('dateOfBirth').setValidators(Validators.required);
  }

  setOtherInsurancesValidations(insurancesFormGroup: FormGroup): void {
    insurancesFormGroup.get('tafInsurer').setValidators(Validators.required);
    insurancesFormGroup.get('nameInsurer').setValidators(Validators.required);
    insurancesFormGroup.get('dateOfCommencement').setValidators(Validators.required);
    insurancesFormGroup.get('sumAssured').setValidators(Validators.required);
    insurancesFormGroup.get('willCancelPolicy').setValidators(Validators.required);
  }

  createAnnuityRateValidator(maxAnnuityRate: number): ValidatorFn {
    return (control: FormControl) => {
      const annuityRate = control.value ? control.value.replace(',', '.') * 1 : 0;
      if (annuityRate > maxAnnuityRate || annuityRate < 1) {
        return {
          type: 'pattern',
          message: this.translateService.instant('Common.MaxAnnuityRate'),
        };
      } else {
        return null;
      }
    };
  }

  occupationValidator(): ValidatorFn {
    return (control: FormControl) => {
      if (control.value === null) {
        return {
          type: 'pattern',
          message: this.translateService.instant('Common.OccupationValidationMessage'),
        };
      }
      return null;
    };
  }

  monthlyAmountValidator(minValue: number, maxValue: number, thousandSeparator: string): ValidatorFn {
    return (control: FormControl) => {
      let { value } = control;
      if (thousandSeparator) {
        value = replaceThousandSeparatorAndRemovingEuroPrefix(control.value, thousandSeparator);
      }
      if (value && (value < minValue || value > maxValue)) {
        return {
          type: 'pattern',
          message: this.translateService.instant('Common.MonthlyAmountValidationMessage'),
        };
      }
      return null;
    };
  }

  dateOfBirthValidator(maxStartAge: number, maxEndAge: number): ValidatorFn {
    return (control: FormControl) => {
      const today = new Date();
      const maxDateForStartPolicy = new Date(today.setMonth(today.getMonth() + 3));
      const insuredFullYears = maxDateForStartPolicy.getFullYear() - new Date(control.value).getFullYear();

      if (insuredFullYears < ValidationService.MIN_AGE) {
        return {
          type: 'pattern',
          message: this.translateService.instant('Common.InsuredMinAgeValidationMessage'),
        };
      } else if (insuredFullYears > maxEndAge) {
        return {
          type: 'pattern',
          message: this.translateService.instant('Common.InsuredMaxAgeValidationMessage', {maxEndAge}),
        };
      }
      return null;
    };
  }

  maxSumAssured(maxValue: number, thousandSeparator: string): ValidatorFn {
    return (control: FormControl) => {
      let { value } = control;
      if (thousandSeparator) {
        value = replaceThousandSeparatorAndRemovingEuroPrefix(control.value, thousandSeparator);
      }
      if (value > maxValue) {
        return {
          max: true,
        };
      }
      return null;
    };
  }

  setPersonalDetailsValidators(insuredFormGroup: FormGroup): void {

    insuredFormGroup.get('insuranceSomewhereElse').setValidators([Validators.required]);
    insuredFormGroup.get('deductPremiumFromTaxes').setValidators([Validators.required]);
    insuredFormGroup.get('averageYearIncome').setValidators(
      [Validators.required, this.averageYearIncomeValidator(ValidationService.MIN_MONTH_AMOUNT, ValidationService.thousandSeparator)]
    );
    insuredFormGroup.get('monthlyExpenses').setValidators(
      [Validators.required, this.averageYearIncomeValidator(ValidationService.MIN_MONTH_AMOUNT, ValidationService.thousandSeparator)]
    );
    if (insuredFormGroup.get('insuranceSomewhereElse').value === OptionsEnum.Yes) {
      insuredFormGroup.get('companyName').setValidators(Validators.required);
    } else {
      insuredFormGroup.get('companyName').clearValidators();
      insuredFormGroup.get('companyName').setErrors(null);
    }
    if (insuredFormGroup.get('insuranceSomewhereElse').value === OptionsEnum.Yes &&
      insuredFormGroup.get('insurerWillCancelInsurance').value === OptionsEnum.Yes
    ) {
      insuredFormGroup.get('cancellationDate').setValidators(Validators.required);
    } else {
      insuredFormGroup.get('cancellationDate').clearValidators();
      insuredFormGroup.get('cancellationDate').setErrors(null);
    }
  }

  averageYearIncomeValidator(MIN_MONTH_AMOUNT: number, thousandSeparator: string): ValidatorFn {
    return (control: FormControl) => {
      let { value } = control;
      if (value && thousandSeparator) {
        value = replaceThousandSeparatorAndRemovingEuroPrefix(value, thousandSeparator);
      }

      if (Number.isInteger(value) && value < MIN_MONTH_AMOUNT) {
        return {
          type: 'pattern',
          message: this.translateService.instant('Common.AverageYearIncomeErrorMessage'),
        };
      }
      return null;
    };
  }

  urlValidator(): ValidatorFn {
    return (control: FormControl) => {
      if (control.value && !Constants.urlRegexpPattern.test(control.value)) {
        return {
          type: 'pattern',
          message: this.translateService.instant('Configurations.ValidationUrl'),
        };
      }
      return null;
    };
  }
  maxTextValidator(): ValidatorFn {
    return (control: FormControl) => {
      if (control.value && control.value.length > 500) {
        return {
          type: 'pattern',
          message: this.translateService.instant('Configurations.ValidationMaxTextLength'),
        };
      }
      return null;
    };
  }
}
